import { crewAverageRating, memberIdsInArchive } from './watchArchive.js'
import { joinableId } from './watch.js'
import { TARGET_WATCHED } from './watchRequests.js'

// The Goated screen's own pure selectors — HANDOFF §6 ("start" in the
// prototype), brief V2-8c. No React here, on purpose — the same split
// `watchGame.js` already keeps for its own screen: derivation over the
// already-loaded archive `titles[]`, rendering is `WatchGoated.jsx`'s job.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 `crewAverageRating` IS IMPORTED, NEVER RE-DERIVED — CLAUDE.md gotcha 21
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-291 (Jordan, 2026-09-25): the crew average leaves a STOPPED member's
// rating out, and counts a WATCHING member's. That rule lives in exactly one
// place, `watchArchive.js::crewAverageRating`, mirrored (never shared) by the
// bot's own `crew_average_rating` and pinned against it by a shared fixture.
// This file calls it; it does not know, and must never re-learn, which
// statuses count.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 "FINISHED" (the 3+ gate, and the stat line's own `n`) IS A DIFFERENT
//    POPULATION FROM "COUNTS TOWARD THE AVERAGE" — DO NOT CONFLATE THEM
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF §6: "a poster grid of titles 3+ members FINISHED, sorted by
// average rating." "Finished" is `status === TARGET_WATCHED` — the same
// population the prototype's own `fin` array models (`D2D Watch v2.dc.html`,
// `entries()`). The crew average is a WIDER population (watching + watched,
// stopped excluded, per P6-291) used only to decide the *value* sorted on.
// A title can therefore have, say, 3 watched members and a 4th still
// watching who also rated it (an airing show somebody rates as they go) —
// the gate and the "N of" count read 3; the average folds in the 4th.
// Keeping these as two separate reads (`finishedCount` vs `crewAverageRating`)
// is deliberate, not an oversight.

/** One title's `members[]`, defensively — the same "never throws, `[]` for
 * anything malformed" discipline every reader in this feature already
 * follows (`watchArchive.js::membersOf`, `watchMember.js::memberEntry`).
 * Not imported from `watchArchive.js`: that helper is module-private there,
 * and this is four lines, not a fact worth a cross-module dependency for
 * (`Person.jsx`'s own stated bar for when a copy is small enough to be one). */
function membersOf(title) {
  const list = title?.members
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

function finishedCount(title) {
  return membersOf(title).filter((member) => member.status === TARGET_WATCHED).length
}

/**
 * "The group loved" — HANDOFF §6: a poster grid of titles 3+ members
 * finished, sorted by average rating. Brief §2:
 *
 * - a title with fewer than 3 `watched` members is never a candidate;
 * - a title with 3+ finishers but no COUNTED rating (`crewAverageRating`
 *   returns `null` — nobody who counts ever rated it) is excluded too, per
 *   the brief's own words: "a title with finishers but no counted rating
 *   isn't on it";
 * - sorted by average, highest first;
 * - **ties break by the number finished, then by title** (`localeCompare`,
 *   ASCII/locale order — stable and deterministic: two titles that also tie
 *   on both keep whatever relative order they arrived in, because `Array.
 *   prototype.sort` is a stable sort per spec);
 * - capped at the top 6.
 *
 * `m`, the design's own "of {m} finished" (its prototype hardcodes 6, the
 * invented `MEMBERS.length` — HANDOFF's own data is fictional, per the
 * brief), is **the number of distinct members with any record in the whole
 * archive** — reused from `memberIdsInArchive` rather than re-counted here,
 * because it is already exactly that population: `set_favorite_rank`/
 * `set_rating` auto-join a member onto a title as `watching` the moment
 * either fact is set on a title they have no other record on
 * (`utils/watch_manager.py::set_favorite_rank`'s own seed-entry branch), so
 * there is no member anywhere in this store with a fact but no status —
 * "any record" and "any status" are the same set here, and
 * `memberIdsInArchive` already derives the latter without guessing at a
 * fixed roster size the way the prototype's own `MEMBERS` constant does.
 */
export function groupLoved(titles) {
  const m = memberIdsInArchive(titles).size

  const candidates = []
  for (const title of titles) {
    const count = finishedCount(title)
    if (count < 3) continue
    const avg = crewAverageRating(title)
    if (avg === null) continue
    candidates.push({
      title,
      avg,
      finishedCount: count,
      stat: `${avg.toFixed(1)} avg · ${count} of ${m} finished`,
    })
  }

  candidates.sort((a, b) => {
    if (b.avg !== a.avg) return b.avg - a.avg
    if (b.finishedCount !== a.finishedCount) return b.finishedCount - a.finishedCount
    return String(a.title?.title ?? '').localeCompare(String(b.title?.title ?? ''))
  })

  return candidates.slice(0, 6)
}

/**
 * "Everyone's top three" — HANDOFF §6: a card per member with a ranked
 * list. Brief §2: "every member with at least one favourite, and their
 * ranks 1-3 in order, each with its title."
 *
 * Only ranks **1, 2 or 3** ever appear, read from each title's own
 * `favorite_rank` rather than assumed from position — `watchMember.js::
 * memberFavorites`'s own reasoning ("read each title's OWN rank rather than
 * assuming `index + 1`, in case a future data anomaly ever makes the ranks
 * non-dense") applies here too, so a hypothetical gap (say, ranks 2 and 3
 * with no 1 on file) reads honestly as two favourites rather than being
 * silently renumbered.
 *
 * Not built by calling `memberFavorites` in a loop: that function reads
 * ONE member's ranks from the whole `archiveShared` blob by id; this reads
 * EVERY member's ranks from an already-extracted `titles[]` in one pass —
 * a different layer (this module's own functions all take `titles`, never
 * `archiveShared`, matching `watchArchive.js`'s own `filterArchiveTitles`/
 * `yearOptions`/`memberIdsInArchive`), not a second implementation of the
 * same question.
 *
 * **Member order — pinned, stated here because there is no derivation-free
 * default:** ascending by the member's raw id string. This layer has no
 * member directory (a display NAME is the caller's own job, the identical
 * split `watchGame.js::boardMemberRows` already keeps — see that module's
 * header), so id order is the only thing a pure selector can promise
 * deterministically without one. Never the design prototype's own fixed
 * `MEMBERS` array order (invented data, brief §0), and never "who has the
 * most favourites" or any other value-dependent order, which would make
 * the list reorder itself as favourites change rather than staying put.
 *
 * Each returned entry is `{ memberId, favorites }`, `favorites` an array of
 * `{ title, rank }` sorted ascending by rank — the exact shape `watchMember.
 * js::memberFavorites` already returns per member, so a caller that also
 * has one of those objects handles both without a type check.
 */
export function topThree(titles) {
  const byMember = new Map()

  for (const title of titles) {
    for (const member of membersOf(title)) {
      const rank = member.favorite_rank
      if (typeof rank !== 'number' || !Number.isFinite(rank) || rank < 1 || rank > 3) continue
      const memberId = joinableId(member.id)
      if (memberId === null) continue
      if (!byMember.has(memberId)) byMember.set(memberId, [])
      byMember.get(memberId).push({ title, rank })
    }
  }

  const memberIds = [...byMember.keys()].sort()

  return memberIds.map((memberId) => ({
    memberId,
    favorites: byMember.get(memberId).sort((a, b) => a.rank - b.rank),
  }))
}
